import { AnimatePresence, motion } from "motion/react"

import { STAGE_SPRING } from "@/components/coach-stage"
import { PageContainer } from "@/components/page-container"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { greetingFor } from "@/lib/greeting"
import { initialsOf } from "@nl/shared"

import {
  ComposerSlot,
  ProgressPanel,
  RecentList,
  SpeakButton,
  TileGrid,
  moduleRows,
  type StartLayoutProps,
} from "./shared"

/**
 * Draft "Geteilt": the header is cut in two, the coach on a picture to the
 * left, the invitation on warm grey to the right. Red only as an accent, blue
 * for the graphics, modules in a fixed grid.
 */
export function SplitLayout(p: StartLayoutProps) {
  const rows = moduleRows(p.recommendations)
  return (
    <>
      <section className="w-full bg-[var(--surface-alt)]">
        <div className="mx-auto grid w-full max-w-[1100px] md:grid-cols-[5fr_7fr]">
          <div className="relative min-h-[300px] overflow-hidden md:min-h-[460px]">
            <img
              src="/fotos/hero-speicherstadt.jpg"
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover dark:opacity-75"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"
            />
            <div className="absolute inset-x-0 bottom-0 flex items-end gap-4 p-7 text-white">
              <Avatar className="size-20 shrink-0 ring-2 ring-white/80">
                {p.coach?.portraitUrl ? (
                  <AvatarImage src={p.coach.portraitUrl} alt="" />
                ) : null}
                <AvatarFallback className="bg-accent text-[20px] font-medium text-primary">
                  {p.coach ? initialsOf(p.coach.name) : ""}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 pb-1">
                <div className="text-[11px] font-bold tracking-[0.06em] uppercase text-white/75">
                  Dein KI-Coach
                </div>
                <div className="font-display text-[22px] leading-tight font-medium tracking-tight">
                  {p.coachName}
                </div>
                {p.coach?.headline ? (
                  <div className="mt-0.5 truncate text-[13px] text-white/80">
                    {p.coach.headline}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <motion.div
            layoutId="coach-card"
            transition={STAGE_SPRING}
            className="flex flex-col justify-center px-8 py-10 md:px-12"
          >
            <AnimatePresence mode="wait" initial={false}>
              {p.voice ? (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="overflow-hidden rounded-xl border border-[var(--rule)] bg-card"
                >
                  {p.voice}
                </motion.div>
              ) : (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="eyebrow">Dein Coach hört zu</div>
                  <h1 className="mt-3 font-display text-[30px] leading-[1.12] font-medium tracking-tight sm:text-[34px]">
                    {greetingFor(new Date())}
                    {p.firstName ? `, ${p.firstName}` : ""}.
                    <br />
                    <span className="text-foreground/65">Woran möchtest du heute arbeiten?</span>
                  </h1>
                  {p.standHint ? (
                    <p className="mt-2.5 text-[13.5px] text-muted-foreground">
                      {p.standHint}
                    </p>
                  ) : null}
                  <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
                    <ComposerSlot>{p.composer}</ComposerSlot>
                    <SpeakButton coachName={p.coachName} onSpeak={p.onSpeak} tone="outline" />
                  </div>
                  <p className="mt-3 text-[12.5px] text-muted-foreground">
                    Schreib mir oder sprich live mit mir, wie am Telefon.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <PageContainer className="mt-10 pb-14">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-9">
            {rows.map((row) => (
              <TileGrid key={row.title} row={row} shape="wide" cols={3} />
            ))}
          </div>
          <aside className="space-y-8">
            <ProgressPanel
              resume={p.resume}
              completedCount={p.completedCount}
              accent="blue"
            />
            <RecentList
              entries={p.entries}
              isLoading={p.entriesLoading}
              coachName={p.coachName}
            />
          </aside>
        </div>
      </PageContainer>
    </>
  )
}
